import { Component, OnInit, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { CourierService } from './../../../shared/services/courier.service';
import { GetOdometerValueRequest, GetOdometerValueResponse, PostEndVoucherRequest, GetActiveVoucherResponse } from './../../../shared/models/courier.models';
import { ActiveUserLocalDataService } from './../../../shared/services/active-user-local-data.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  templateUrl: 'finish-voucher.component.html',
  styleUrls: ['./finish-voucher.component.scss']
})
export class FinishVoucherComponent implements OnInit {

  odometerResponse: GetOdometerValueResponse | null = null;
  startOdometerValue: number = 0;
  startHours: number = 0;
  startMinutes: number = 0;
  workStartTime: Date | null = null;
  chosenDate: Date = new Date();
  isButtonDisabled: boolean = false;
  hours: number[] = Array.from({ length: 24 }, (_, i) => i);
  minutes: number[] = Array.from({ length: 12 }, (_, i) => i * 5);
  comment: string = '';
  showPopup: boolean = false;
  popupMessage: string = 'odometr end value is invalid';
  loading: boolean = true;
  loadCounter: number = 0;
  isPopupVisible = false;
  isMobile: boolean = false;
  previousOdometerValue: number | null = null;
  odometerDifference: number | null = null;

  constructor(
    private courierService: CourierService,
    private activeUserLocalDataService: ActiveUserLocalDataService,
    private router: Router,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.checkIfMobile();
    this.loadOdometer();
    this.loadComment();
    this.loadActiveVoucher();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkIfMobile();
  }

  private checkIfMobile() {
    this.isMobile = window.innerWidth <= 768;
  }

  loadOdometer() {
    const activeVoucher = this.activeUserLocalDataService.getActiveCourierVoucher();
    if (activeVoucher) {
      const request: GetOdometerValueRequest = { carId: activeVoucher.carsId };
      this.courierService.getOdometrValue(request).subscribe((response: GetOdometerValueResponse) => {
        if (response) {
          this.odometerResponse = response;
          const currentTime = new Date();
          this.startHours = currentTime.getHours();
          this.startMinutes = Math.round(currentTime.getMinutes() / 5.0) * 5;

          if (this.startMinutes === 60) {
            this.startMinutes = 0;
            this.startHours = (this.startHours + 1) % 24;
          }
        } else {
          this.navigateToVoucher();
        }
        this.checkLoading();
      }, error => {
        console.error('Error fetching odometer value:', error);
        this.navigateToVoucher();
      });
    } else {
      this.navigateToVoucher();
    }
  }

  loadComment() {
    const activeVoucher = this.activeUserLocalDataService.getActiveCourierVoucher();
    if (activeVoucher) {
      const voucherId = activeVoucher.id;
      this.courierService.getComment({ voucherId }).subscribe(response => {
        if (response.comment.length > 0) {
          this.comment = response.comment[0].driverComment || '';
        } else {
          this.comment = '';
        }
        this.checkLoading();
      }, error => {
        console.error('Error fetching comments:', error);
        this.navigateToVoucher();
      });
    } else {
      this.navigateToVoucher();
    }
  }

  loadActiveVoucher() {
    const request = {};
    this.courierService.getActiveVouchers(request).subscribe((response: GetActiveVoucherResponse) => {
      if (response && response.voucher) {
        this.activeUserLocalDataService.setActiveCourierVoucher(response.voucher);
        this.workStartTime = response.voucher.workStartTime ? new Date(response.voucher.workStartTime) : null;
        this.previousOdometerValue = response.voucher.odometerStartVal ?? 0;
        this.startOdometerValue = this.previousOdometerValue;
        this.computeOdometerDifference();
      } else {
        this.navigateToVoucher();
      }
      this.checkLoading();
    }, error => {
      console.error('Error fetching active voucher:', error);
      this.navigateToVoucher();
    });
  }

  checkLoading() {
    this.loadCounter++;
    if (this.loadCounter >= 3) {
      this.loading = false;
    }
  }

  dayBack() {
    const changedDate = new Date(this.chosenDate);
    changedDate.setDate(changedDate.getDate() - 1);
    this.chosenDate = changedDate;
  }
  
  dayForward() {
    const changedDate = new Date(this.chosenDate);
    changedDate.setDate(changedDate.getDate() + 1);
    this.chosenDate = changedDate;
  }
  finishVoucher() {
    const activeVoucher = this.activeUserLocalDataService.getActiveCourierVoucher();
    if (activeVoucher) {
      const finalDate = new Date(this.chosenDate);
      finalDate.setHours(this.startHours);
      finalDate.setMinutes(this.startMinutes);
      finalDate.setSeconds(0);
      finalDate.setMilliseconds(0);
  
      if (this.workStartTime && finalDate < this.workStartTime) {
        this.popupMessage = this.translateService.instant('EndTimeCannotBeBeforeWorkStartTimePleaseEnterAValidEndTime');
        this.showPopup = true;
        return;
      }
  
      const voucherStartOdometer = activeVoucher.odometerStartVal ?? 0;
      const odometerDifference = this.startOdometerValue - voucherStartOdometer;

      if (odometerDifference > 1500) {
        this.popupMessage = this.translateService.instant('EnteredOdometerVlueExceeds1500KmPleaseEnterAValidOdometerValue');
        this.showPopup = true;
        return;
      }
  
      if (this.startOdometerValue > voucherStartOdometer) {
        const request: PostEndVoucherRequest = {
          odometerEndVal: this.startOdometerValue,
          workEndTime: finalDate,
          driverComment: this.comment,
          voucherId: activeVoucher.id
        };
  
        this.courierService.postEnd(request).subscribe(() => {
          this.activeUserLocalDataService.setActiveCourierVoucher(null);
          this.router.navigate(['/voucher']);
        });
      } else {
        this.popupMessage = this.translateService.instant('PleaseEnterAValidOdometerValue');
        this.showPopup = true;
      }
    }
  }

  onOdometerChanged(e: { value?: any }) {
    const value = e && 'value' in e ? (e as any).value : undefined;
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      this.startOdometerValue = isNaN(parsed) ? 0 : parsed;
    } else {
      this.startOdometerValue = value ?? 0;
    }
    this.computeOdometerDifference();
  }

  closePopup() {
    this.showPopup = false;
  }

  navigateToVoucher() {
    this.router.navigate(['/voucher']);
  }

  private computeOdometerDifference(): void {
    if (this.previousOdometerValue != null && this.startOdometerValue != null) {
      this.odometerDifference = this.startOdometerValue - this.previousOdometerValue;
    } else {
      this.odometerDifference = null;
    }
  }
}